import { ChainConfig, Stable } from 'core/constants'
import { Sale } from 'core/logic/asset/asset.types'
import { OnChainOfferType } from 'core/logic/token/token.types'
import {
  OfferForm,
  PREDEFINED_DEADLINES,
} from 'pages/Authenticated/Marketplace/CreateOffer/CreateOffer.types'
import IMarketplace, { Offers } from 'service/IMarketplace'
import {
  NftSwapV4,
  SignedNftOrderV4,
  UserFacingERC1155AssetDataSerializedV4,
  UserFacingERC20AssetDataSerializedV4,
} from '@onepercentio/nft-swap-sdk'
import {
  toContractNumber,
  toContractNumberBN,
} from 'core/helpers/convertNumbers'
import addToDate from 'date-fns/add'
import {
  createCustodyOffer,
  disableOrder,
  getMarketplaceOrder,
  getMarketplaceOrders,
  getMarketplaceOrdersQuery,
  write0xOrder,
} from 'core/modules/firebase/service/marketplace'
import { add } from 'date-fns'
import BN from 'bignumber.js'
import { BaseGalleryType } from 'core/logic/gallery/gallery.types'
import OnChainBase from 'service/OnChainBase'
import {
  approveContract,
  doMarketplacePurchase,
  waitForOkenTransactionToEnd,
} from 'core/modules/firebase/service'
import { useDynamicMarketplaceOffers } from 'context/Marketplace'
import ProgressDispatcher from 'openspace/service/Progress'
import { t } from 'translate/i18n'

export default abstract class ZeroXMarketplace
  extends OnChainBase
  implements IMarketplace
{
  abstract buyOffer(sale: Pick<Sale, 'offerId'>): Promise<void | string>
  abstract setCurrencyAllowance(
    currency: Stable['symbol'],
    allowThisMuch: number
  ): Promise<void>
  abstract approveMarketplaceToManageTokens(
    _galleryId: string,
    tokenContract: string
  ): Promise<void>
  abstract allowanceForCurrency(
    currency: string,
    allowanceRequired: number
  ): Promise<number>
  abstract createOffer(
    tokenContract: string,
    offerRequest: OfferForm
  ): Promise<string>
  abstract updateOffer(offerId: string, offer: OfferForm): Promise<void>
  abstract removeOffer(_offerId: string): Promise<void>
  private chain!: Pick<ChainConfig, 'id'>
  protected nftSwapForRead!: NftSwapV4
  protected galleries!: Pick<
    BaseGalleryType,
    'id' | 'marketplace' | 'smartContract'
  >[]

  setChainId(chainId: ChainConfig['id']) {
    this.chain = { id: chainId }
  }

  setGalleries(
    galleries: Pick<BaseGalleryType, 'id' | 'marketplace' | 'smartContract'>[]
  ) {
    this.galleries = galleries
  }

  async setWallet(wallet?: string) {
    this.wallet = wallet
    this.nftSwapForRead = await this.getNFTSwap()
  }

  protected async getNFTSwap(wallet?: string) {
    const { NftSwapV4: NFTSwapLibrary } = await import(
      '@onepercentio/nft-swap-sdk'
    )
    /** The project is using the @4 version because of use-wallet and trader needs @5... I won't be upgrading right now */
    const providers = await import('@ethersproject/providers')

    if (wallet) {
      const provider = new providers.Web3Provider(
        this.web3ForWrite.currentProvider as any
      )
      const signer = provider.getSigner(wallet) as any
      return new NFTSwapLibrary(provider, signer, this.chain.id)
    } else {
      const provider = new providers.Web3Provider(
        this.web3ForRead.currentProvider as any
      )
      const signer = provider.getSigner(wallet) as any
      return new NFTSwapLibrary(provider, signer, this.chain.id)
    }
  }

  private unknownOrderError() {
    new Error(`The type of 0x order is not supported yet`)
  }

  setAvailableCurrencies(currencies: typeof this.currency) {
    this.currency = currencies
  }

  private parseFirebaseOffers(
    firebaseOffers: Awaited<ReturnType<typeof getMarketplaceOrders>>
  ) {
    const onChainOffers = firebaseOffers.map((firebaseOffer) => ({
      ...firebaseOffer.order0x,
      createdAt: new Date(firebaseOffer.created),
      user: firebaseOffer.userId,
      id: firebaseOffer.id,
      cid: firebaseOffer.orderParams.asset.cid,
      fulfillment: firebaseOffer.fulfillment,
      comment: firebaseOffer.orderRequest.description,
    }))

    const parsedOffers: OnChainOfferType[] = []

    for (let offer of onChainOffers) {
      const [tokenCurrencySymbol] = Object.entries(this.currency).find(
        (c) => c[1].address.toLowerCase() === offer.erc20Token.toLowerCase()
      )!

      // const orderInfo = await this.nftSwapForRead.getERC1155OrderInfo(offer)
      // const howManySoldUnits = orderInfo.amount.sub(orderInfo.remaining)

      let price!: number

      if ('erc1155TokenAmount' in offer) {
        price = new BN(offer.erc20TokenAmount.toString())
          .plus(new BN(offer.fees[0].amount as string))
          .div(new BN(offer.erc1155TokenAmount.toString()))
          .toNumber()
      } else if ('erc721Token' in offer) {
        price = new BN(offer.erc20TokenAmount.toString())
          .plus(new BN(offer.fees[0].amount as string))
          .toNumber()
      } else {
        throw this.unknownOrderError()
      }

      const partialOffer: Partial<OnChainOfferType> = {
        active: true,
        endTime: add(offer.createdAt, {
          seconds: Number(offer.expiry),
        }).getTime(),
        fromTenant: true,
        id: offer.id,
        paymentOptions: {
          paymentToken: tokenCurrencySymbol,
          price,
          beneficiary: offer.maker.toLowerCase(),
        },
        metadata: {
          id0x: `${offer.maker}:${offer.nonce.toString()}`,
          makerWallet: offer.maker,
          deliveryId: offer.fulfillment?.deliveryId,
          firebaseUID: offer.user,
          comments: offer.comment,
        },
      }
      /** ERC1155 */
      if ('erc1155TokenId' in offer) {
        partialOffer.deliveryOptions = {
          tokenId: Number(offer.erc1155TokenId),
        }
        partialOffer.supply = Number(offer.erc1155TokenAmount)
      } else if ('erc721TokenId' in offer) {
        partialOffer.deliveryOptions = {
          tokenId: Number(offer.erc721TokenId),
          cid: offer.cid,
        }
        partialOffer.supply = Number(1)
      }
      parsedOffers.push(partialOffer as OnChainOfferType)
    }

    return parsedOffers.sort(
      (a, b) => a.paymentOptions.price - b.paymentOptions.price
    )
  }

  /**
   * Get the marketplace offers from firestore
   * The offers must have the gallery smartcontract
   */
  async getOffersForCollection(galleryId: string): Promise<OnChainOfferType[]> {
    const firebaseOffers = await getMarketplaceOrders(galleryId)

    return this.parseFirebaseOffers(firebaseOffers)
  }

  getQueryForCollectionOffers(
    galleryId: string,
    filter: Parameters<typeof useDynamicMarketplaceOffers>[0]
  ): (
    page: number,
    pageSize: number | 'all',
    currItems?: Offers | undefined
  ) => Promise<{
    finished: boolean
    items: Offers
    totalItems: number
  }> {
    const query = getMarketplaceOrdersQuery(galleryId, filter)
    let totalItems: number
    return async (page, size, curr = []) => {
      const latestItem = curr[page * (size as number) - 1]
      const newItems = this.parseFirebaseOffers(
        await query.docs(page, size, latestItem?.id)
      )

      const combinedItems = [...curr, ...newItems]
      if (totalItems === undefined) totalItems = await query.total()
      return Promise.resolve({
        finished: combinedItems.length === totalItems,
        items: combinedItems,
        totalItems: totalItems,
      })
    }
  }
  async getFeeForCollection(galleryId: string): Promise<number> {
    return (
      this.galleries.find((g) => g.id === galleryId)!.marketplace!.fee! / 100
    )
  }
  protected async offerCreationData(
    tokenContract: string,
    offerRequest: OfferForm
  ) {
    const galleryMarketplaceConfig = this.galleries.find(
      (g) => g.id === offerRequest.asset.galleryId
    )!.marketplace!
    if (Object.keys(offerRequest.amount).length !== 1)
      throw new Error(`This implementation is not ready for multi currency yet`)
    let duration: Duration
    switch (offerRequest.selectedDeadline) {
      case PREDEFINED_DEADLINES.A_MONTH:
        duration = { months: 1 }
        break
      case PREDEFINED_DEADLINES.TWO_MONTHS:
        duration = { months: 2 }
        break
      case PREDEFINED_DEADLINES.THREE_MONTHS:
        duration = { months: 3 }
        break
    }
    const [currencySymbol, currencyInfo] = Object.entries(
      offerRequest.amount
    )[0]
    const currencyConfig = this.currency[currencySymbol]
    const amountToPay = toContractNumberBN(
      new BN(currencyInfo!.amount)
        .multipliedBy(new BN(offerRequest.selectedQuantity))
        .toString(),
      currencyConfig.decimals
    )
    const fee = new BN(galleryMarketplaceConfig.fee!).div(100)
    const feeToPay = amountToPay.multipliedBy(fee)

    return {
      gallery: {
        contract: tokenContract,
        recipient: galleryMarketplaceConfig.address!,
      },
      prices: { total: amountToPay, fee: feeToPay },
      offerDuration: duration,
      currencyAddress: currencyConfig.address,
    }
  }
  /** Will ask if ERC1155 allows 0x to movement tokens */
  async canMarketplaceManageTokens(
    tokenContract: Contract.ERC1155
  ): Promise<boolean> {
    const approval = await this.nftSwapForRead.loadApprovalStatus(
      {
        type: 'ERC1155',
        tokenAddress: tokenContract.options.address,
        tokenId: '',
        amount: '',
      },
      this.wallet!
    )

    return approval.contractApproved
  }

  async getSpecificOffer(offerId: string) {
    return this.parseFirebaseOffers([await getMarketplaceOrder(offerId)])[0]
  }
}

export class CustodyZeroXMarketplace extends ZeroXMarketplace {
  async removeOffer(_offerId: string) {
    const offer = await getMarketplaceOrder(_offerId)
    await disableOrder(offer.galleryId, offer.id)
  }
  updateOffer(offerId: string, offer: OfferForm): Promise<void> {
    throw new Error('Method not implemented.')
  }
  async createOffer(
    tokenContract: string,
    offerRequest: OfferForm
  ): Promise<string> {
    ProgressDispatcher.dispatchHandle(
      'create_offer',
      t('offer.creation.steps.creating.progress.creatingOffer'),
      2
    )
    const token = Object.keys(offerRequest.amount)[0]
    const { id: orderId } = await createCustodyOffer(
      offerRequest.selectedQuantity,
      offerRequest.asset.tokenId.toString(),
      offerRequest.cid,
      {
        [token]: { amount: offerRequest.amount[token]!.amount!, checked: true },
      },
      offerRequest.selectedDeadline,
      process.env.REACT_APP_TENANT_IDENTITY_ID,
      this.galleries.find((g) => g.smartContract!.address === tokenContract)!
        .id,
      offerRequest.description,
      offerRequest.asset.id
    )
    return orderId
  }
  async allowanceForCurrency(
    _currency: string,
    _allowanceRequired: number
  ): Promise<number> {
    return Number.MAX_SAFE_INTEGER
  }
  async buyOffer(sale: Sale): Promise<string> {
    ProgressDispatcher.dispatchHandle(
      'step_approval',
      t('offer.buy.progress.buyingOffer'),
      2
    )
    return (
      await doMarketplacePurchase(
        sale.wallet,
        sale.offerId,
        sale.price,
        sale.quote?.id
      )
    ).id
  }
  setCurrencyAllowance(currency: string, allowThisMuch: number): Promise<void> {
    throw new Error('This should not be called')
  }
  async approveMarketplaceToManageTokens(
    _galleryId: string,
    tokenContract: string
  ): Promise<void> {
    ProgressDispatcher.dispatchHandle(
      'create_offer',
      t('offer.creation.steps.creating.progress.authorizing'),
      0
    )
    const tId = await approveContract(_galleryId, tokenContract, true)
    ProgressDispatcher.dispatchHandle(
      'create_offer',
      t('generic.waitingAuthorization'),
      1
    )
    await waitForOkenTransactionToEnd(tId)
  }
}

export class ExternalZeroXMarketplace extends ZeroXMarketplace {
  protected async createSignedOffer(
    tokenContract: string,
    offerRequest: OfferForm
  ): Promise<SignedNftOrderV4> {
    const { gallery, prices, currencyAddress, offerDuration } =
      await this.offerCreationData(tokenContract, offerRequest)

    const order = this.nftSwapForWrite.buildOrder(
      {
        tokenId: String(offerRequest.tokenId),
        amount: String(offerRequest.selectedQuantity),
        tokenAddress: gallery.contract,
        type: 'ERC1155',
      } as UserFacingERC1155AssetDataSerializedV4,
      {
        type: 'ERC20',
        amount: prices.total.minus(prices.fee).toString(),
        tokenAddress: currencyAddress,
      } as UserFacingERC20AssetDataSerializedV4,
      this.wallet!,
      {
        expiry: addToDate(new Date(), offerDuration),
        fees: [
          {
            amount: prices.fee.toString(),
            recipient: gallery.recipient,
          },
        ],
      }
    )
    const signedOrder = await this.nftSwapForWrite.signOrder(order)
    return signedOrder
  }
  async updateOffer(offerId: string, offer: OfferForm): Promise<void> {
    throw new Error('Method not implemented.')
  }
  async removeOffer(_offerId: string) {
    const offer = await getMarketplaceOrder(_offerId)
    await (
      await this.nftSwapForWrite.cancelOrder(
        offer.order0x.nonce,
        'erc1155Token' in offer ? 'ERC1155' : 'ERC721'
      )
    ).wait()
    await disableOrder(offer.galleryId, offer.id)
  }
  async createOffer(
    tokenContract: string,
    offerRequest: OfferForm
  ): Promise<string> {
    const signedOffer = await this.createSignedOffer(
      tokenContract,
      offerRequest
    )
    const { transactionId: id } = await write0xOrder(
      signedOffer,
      offerRequest.asset
    )

    return id
  }
  private nftSwapForWrite!: NftSwapV4
  /**
   * Check if 0x is authorized to manage the tokens
   */
  async allowanceForCurrency(
    currency: string,
    allowanceRequired: number
  ): Promise<number> {
    const approvalForCurrency = await this.nftSwapForRead.loadApprovalStatus(
      {
        type: 'ERC20',
        amount: toContractNumber(
          String(allowanceRequired),
          this.currency[currency].decimals
        ),
        tokenAddress: this.currency[currency].address,
      },
      this.wallet!
    )
    if (approvalForCurrency.contractApproved) return allowanceRequired
    else return 0
  }
  async approveMarketplaceToManageTokens(
    _galleryId: string,
    tokenContract: string
  ) {
    const approval = await this.nftSwapForWrite.approveTokenOrNftByAsset(
      {
        type: 'ERC1155',
        tokenAddress: tokenContract,
        tokenId: '',
        amount: '',
      },
      this.wallet!
    )
    await approval.wait()
  }
  async setCurrencyAllowance(currency: string, allowThisMuch: number) {
    await this.nftSwapForWrite.approveTokenOrNftByAsset(
      {
        type: 'ERC20',
        amount: toContractNumber(
          String(allowThisMuch),
          this.currency[currency].decimals
        ),
        tokenAddress: this.currency[currency].address,
      },
      this.wallet!
    )
  }
  override async setWallet(wallet?: string | undefined): Promise<void> {
    super.setWallet(wallet)
    if (this.wallet) this.nftSwapForWrite = await this.getNFTSwap(this.wallet)
  }
  protected async buySignedOrder(signedOrder: SignedNftOrderV4) {
    const fillTx = await this.nftSwapForWrite.fillSignedOrder(signedOrder, {
      erc1155TokenAmount: 1,
    })
    await fillTx.wait()
  }
  async buyOffer(sale: Pick<Sale, 'offerId'>): Promise<void> {
    const signedOrder = await getMarketplaceOrder(sale.offerId)
    return await this.buySignedOrder({
      ...signedOrder.order0x,
      signature: signedOrder.signedOrder,
    })
  }
}
